import { z } from "zod";

import { BannerSection } from "@widgets/banner";

import { GridMediaListSection } from "@features/home-grid-cards/model";

import { ColorIconSchema } from "@shared/content-types/schemas/common.schema";
import { StrapiMediaSchema } from "@shared/content-types/schemas/media.schema";
import { TitleWithIconSchema } from "@shared/content-types/schemas/pageHero.schema";
import {
  AboutMoreSection,
  ArtSectionsSection,
  CulturalObjectsSlider,
  EventsSliderSection,
  HowToGetSection,
  MainAboutSection,
  SliderSection,
} from "@shared/content-types/schemas/sections.schema";
import { SeoSchema } from "@shared/content-types/schemas/seo.schema";
import { createComponentSchemas } from "@shared/content-types/utils";

import { HowToGetPageSchema } from "@/_pages/how-to-get/model";

const StoryImageBaseSchema = z.object({
  image: StrapiMediaSchema,
});

export const StoryImage = createComponentSchemas(
  "sections.story-image",
  StoryImageBaseSchema,
);

const StoriesSectionSchema = z.object({
  isVisible: z.boolean().optional().default(true),
  title: TitleWithIconSchema,
  images: StoryImage.base.array(),
});

export const StoriesSection = createComponentSchemas(
  "sections.main-stories-section",
  StoriesSectionSchema,
);

export const HomeBlocksSchema = z.array(
  z.discriminatedUnion("__component", [
    BannerSection.dynamic,
    EventsSliderSection.dynamic,
    StoriesSection.dynamic,
    AboutMoreSection.dynamic,
    HowToGetSection.dynamic,
    GridMediaListSection.dynamic,
    MainAboutSection.dynamic,
    ArtSectionsSection.dynamic,
    SliderSection.dynamic,
    CulturalObjectsSlider.dynamic,
  ]),
);

export const HomeHeroSchema = z.object({
  id: z.number().positive(),
  link: z.string().min(1),
  date: z.string().min(1),
  buttonText: z.string().min(1).optional().nullable(),
  backgroundImage: StrapiMediaSchema,
  backgroundImageMobile: StrapiMediaSchema,
  title: TitleWithIconSchema,
  imageDarkening: z.string().min(1),
});

export type HomeBlock = z.infer<typeof HomeBlocksSchema>[number];

export const HomePageSchema = z.object({
  title: z.string().min(1),
  pageIcon: ColorIconSchema,
  blocks: HomeBlocksSchema,
  hero: z.object({
    AITexts: z.array(z.object({ text: z.string().min(1) })),
    heroSlide: z.array(HomeHeroSchema),
  }),
  htg: HowToGetSection.base,
  seo: SeoSchema.optional().nullable(),
});

export const HomePageDtoSchema = z.object({
  data: HomePageSchema,
});

export type HomePageType = z.infer<typeof HomePageSchema>;
export type HomeHeroType = z.infer<typeof HomeHeroSchema>;
